/**
 * 采集批次卡片组件
 * 用于展示单个采集批次（素材包）的信息
 * 整个卡片可点击跳转详情，删除按钮悬停显示
 */

import type { ReactElement } from 'react'
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Trash2, Loader2, AlertCircle, FolderOpen, Clock, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import type { CrawlBatch } from '@/types/crawlBatch'
import { deleteCrawlBatch } from '@/services/crawlBatchService'

/**
 * 组件属性
 */
interface CrawlBatchCardProps {
  /** 批次数据 */
  batch: CrawlBatch
  /** 刷新列表回调 */
  onRefresh?: () => void
}

/**
 * 格式化相对时间
 * @param dateString - ISO 8601 时间字符串
 * @returns 格式化后的相对时间
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) {
    return '刚刚'
  }
  if (diffMin < 60) {
    return `${String(diffMin)} 分钟前`
  }
  if (diffHour < 24) {
    return `${String(diffHour)} 小时前`
  }
  if (diffDay < 7) {
    return `${String(diffDay)} 天前`
  }

  return date.toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 采集批次卡片组件
 * @param props - 组件属性
 * @returns 批次卡片 ReactElement
 */
export function CrawlBatchCard({
  batch,
  onRefresh,
}: CrawlBatchCardProps): ReactElement {
  const navigate = useNavigate()

  // 删除确认弹窗状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  /**
   * 处理删除确认
   */
  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteCrawlBatch(batch.id)
      setShowDeleteDialog(false)
      onRefresh?.()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : '删除失败，请稍后再试')
    } finally {
      setIsDeleting(false)
    }
  }, [batch.id, onRefresh])

  /**
   * 跳转到批次详情页
   */
  const handleViewDetail = useCallback((): void => {
    void navigate(`/materials/${batch.id}`)
  }, [navigate, batch.id])

  /**
   * 处理删除按钮点击（阻止事件冒泡）
   */
  const handleDeleteClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    setShowDeleteDialog(true)
  }, [])

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        onClick={handleViewDetail}
        className={cn(
          'group relative cursor-pointer rounded-xl border bg-card p-4 shadow-sm transition-all',
          'hover:border-primary/40 hover:shadow-md'
        )}
      >
        {/* 删除按钮 - 桌面端悬停显示，移动端始终显示 */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'absolute right-2 top-2 h-7 w-7 p-0',
            'text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20',
            'sm:opacity-0 sm:group-hover:opacity-100 transition-opacity'
          )}
          onClick={handleDeleteClick}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>

        {/* 图标区域 */}
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <FolderOpen className="h-5 w-5 text-primary" />
        </div>

        {/* 主内容区 */}
        <div className="space-y-3">
          {/* 备注（主要信息） */}
          <h3 className="line-clamp-2 text-sm font-semibold leading-tight">
            {batch.remark || '未命名素材包'}
          </h3>

          {/* 次要信息 */}
          <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />
              <span>{batch.totalCount} 个商品</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatRelativeTime(batch.createdAt)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 删除确认弹窗 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除批次</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>确认要删除这个批次吗？删除后该批次下的所有商品也将被删除，且无法恢复。</p>
                <div className="rounded-lg border bg-muted/50 p-3">
                  <p className="text-sm font-medium text-foreground">
                    {batch.remark || '未命名素材包'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    包含 {batch.totalCount} 个商品
                  </p>
                </div>
                {deleteError !== null && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-sm text-red-600">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{deleteError}</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e): void => {
                e.preventDefault()
                void handleDeleteConfirm()
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                '确认删除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
